import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '../../chart';
import type { ChartBlock } from '../chat-content';

// Default palette when no config is provided
const DEFAULT_COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
];

export function ChartBlockRenderer({ block }: { block: ChartBlock }) {
  // Build ChartConfig from block.config or generate defaults
  const chartConfig = block.config
    ? Object.fromEntries(
        Object.entries(block.config).map(([key, val], i) => [
          key,
          { label: val.label, color: val.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length] },
        ]),
      )
    : Object.fromEntries(
        block.dataKeys.map((key, i) => [
          key,
          { label: key, color: DEFAULT_COLORS[i % DEFAULT_COLORS.length] },
        ]),
      );

  if (block.chartType === 'bar') {
    return (
      <ChartContainer config={chartConfig} className="h-52 w-full">
        <BarChart data={block.data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey={block.xKey} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} width={32} />
          <ChartTooltip content={<ChartTooltipContent />} />
          <ChartLegend content={<ChartLegendContent />} />
          {block.dataKeys.map((key) => (
            <Bar key={key} dataKey={key} fill={`var(--color-${key})`} radius={[3, 3, 0, 0]} />
          ))}
        </BarChart>
      </ChartContainer>
    );
  }

  if (block.chartType === 'line') {
    return (
      <ChartContainer config={chartConfig} className="h-52 w-full">
        <LineChart data={block.data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey={block.xKey} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} width={32} />
          <ChartTooltip content={<ChartTooltipContent />} />
          <ChartLegend content={<ChartLegendContent />} />
          {block.dataKeys.map((key) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={`var(--color-${key})`}
              strokeWidth={2}
              dot={false}
            />
          ))}
        </LineChart>
      </ChartContainer>
    );
  }

  if (block.chartType === 'pie') {
    return (
      <ChartContainer config={chartConfig} className="h-52 w-full">
        <PieChart>
          <ChartTooltip content={<ChartTooltipContent hideLabel />} />
          <ChartLegend content={<ChartLegendContent />} />
          <Pie
            data={block.data}
            dataKey={block.dataKeys[0] ?? 'value'}
            nameKey={block.xKey}
            cx="50%"
            cy="50%"
            outerRadius={80}
          >
            {block.data.map((_, i) => (
              <Cell key={i} fill={DEFAULT_COLORS[i % DEFAULT_COLORS.length]} />
            ))}
          </Pie>
        </PieChart>
      </ChartContainer>
    );
  }

  return null;
}
